/*
 * PROFIT-MARGIN-ADJUSTMENT.JSX - Profit Margin Adjustment Modal Component
 *
 * SUMMARY:
 * Modal component for adjusting tender profit margins with rounding capabilities.
 * Provides real-time calculation updates and preview of original vs adjusted values
 */

import React, { useState, useEffect, useMemo } from "react";
import { Button, Form, Row, Col, Table, Alert, Spinner } from "react-bootstrap";
import { calculateProfitAdjustment } from "../../util/calculations/profit-rounding";
import {
  resetProfitMargin,
  getProfitMarginAuditHistory,
} from "../../actions/project-management.action";
import Modal from "../modal/modal";
import "./ProfitMarginAdjustment.scss";
import { useDispatch } from "react-redux";
import { getProjectSummaryData } from "src/routes/projects/utils/get-project-summary";
import CustomTable from "../table/table";
import { showPercent, toUSD } from "src/util/currency-formatter";
import { formatAdjustmentMethod } from "src/util/adjustment-method-formatter";
import { toDecimal } from "src/util/decimal-utils";

export default function ProfitMarginAdjustment({
  open,
  onClose,
  projectData,
  onApplyAdjustment,
  projectId,
}) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    roundingIncrement: 1000,
    roundingDirection: "UP",
    adjustmentMethod: "EXACT_AMOUNT",
    targetValue: "",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    original: {},
    adjusted: {},
    notes: {},
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [showApplyConfirmation, setShowApplyConfirmation] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  const clearTargetError = () => {
    setErrors((prev) => {
      if (!prev.targetValue) {
        return prev;
      }
      const { targetValue, ...rest } = prev;
      return rest;
    });
  };

  const validateTargetValue = (method, value) => {
    if (!["EXACT_AMOUNT", "MARGIN_PERCENT"].includes(method)) {
      return { error: null, parsedValue: 0 };
    }

    const cleanedValue = String(value).replace(/[$,%\s,]/g, "");
    if (!cleanedValue.trim()) {
      return { error: "Enter a value", parsedValue: NaN };
    }

    const parsedValue = parseNumber(value);
    if (!Number.isFinite(parsedValue)) {
      return { error: "Enter a valid number", parsedValue };
    }

    const decimalIndex = cleanedValue.indexOf(".");
    if (decimalIndex >= 0) {
      const decimalPart = cleanedValue.slice(decimalIndex + 1);
      if (decimalPart.length > 2) {
        return { error: "Use no more than two decimal places", parsedValue };
      }
    }

    if (method === "EXACT_AMOUNT" && parsedValue < 0) {
      return { error: "Value cannot be negative", parsedValue };
    }

    if (method === "MARGIN_PERCENT") {
      if (parsedValue < 0) {
        return { error: "Margin % cannot be negative", parsedValue };
      }
      if (parsedValue > 100) {
        return { error: "Margin % cannot exceed 100", parsedValue };
      }
    }

    return { error: null, parsedValue };
  };

  const sanitizeTargetValue = (value, previousValue = "") => {
    if (value === null || value === undefined) return "";

    const rawInput = String(value);
    let raw = rawInput.replace(/[$,%\s,]/g, "");

    if (raw === "") {
      return "";
    }

    if (raw === ".") {
      return "0.";
    }

    if (/^\d+\.$/.test(raw)) {
      return raw;
    }

    if (/^\d*(\.\d{0,2})?$/.test(raw)) {
      return raw;
    }

    const match = raw.match(/^(\d*)(?:\.(\d{0,2}))?/);
    if (match) {
      const integerPart = match[1] || "0";
      const decimalPart = match[2];

      if (rawInput.includes(".")) {
        if (decimalPart === undefined) {
          return `${integerPart}.`;
        }
        return `${integerPart}.${decimalPart}`;
      }

      return integerPart;
    }

    return previousValue;
  };

  // --- Helpers ---
  const parseNumber = (value = "") => {
    if (value === null || value === undefined) return NaN;
    // Remove commas, currency symbols, and percent signs
    const cleaned = String(value).replace(/[$,%\s,]/g, "");
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  // Pull numeric totals from project data
  const currentTotals = useMemo(() => {
    const totalCostDecimal = toDecimal(projectData?.projectTotal?.totalCost);
    const totalSaleAmountDecimal = toDecimal(
      projectData?.projectTotal?.totalSaleAmount
    );
    const totalCost = totalCostDecimal.toNumber();
    const totalSaleAmount = totalSaleAmountDecimal.toNumber();
    const individualProfitMargin = projectData?.project?.projectMarginValue; // individual profit margin applied on each item
    // Prefer numeric margin from store; if missing, derive
    const totalProfitPercentage =
      totalSaleAmountDecimal.gt(0)
        ? totalSaleAmountDecimal
          .minus(totalCostDecimal)
          .dividedBy(totalSaleAmountDecimal)
          .times(100)
          .toNumber()
        : 0;
    const totalProfit = totalSaleAmountDecimal.minus(totalCostDecimal).toNumber();
    return {
      totalCost,
      totalSaleAmount,
      totalProfitPercentage,
      totalProfit,
      projectTotal: projectData?.projectTotal,
      individualProfitMargin,
    };
  }, [projectData]);

  // Load audit history when modal opens
  useEffect(() => {
    if (open && projectId) {
      loadAuditHistory();
    }
  }, [open, projectId]);

  // Initialize original values whenever project data changes or modal opens
  useEffect(() => {
    setCalculatedValues({
      original: {
        totalCost: currentTotals.totalCost,
        profitMargin: currentTotals.totalProfitPercentage,
        individualProfitMargin: currentTotals.individualProfitMargin,
        tenderTotal: currentTotals.totalSaleAmount,
        difference: "--",
        totalProfit: currentTotals.totalProfit,
      },
      adjusted: {},
      notes: {},
    });
    setErrors({});
  }, [open, currentTotals]);

  const loadAuditHistory = async () => {
    try {
      const result = await dispatch(getProfitMarginAuditHistory(projectId));
      if (result !== undefined) {
        setAuditHistory(result || []);
      }
    } catch (error) {
      console.error("Failed to load audit history:", error);
    }
  };

  // Compute adjusted values in real-time when inputs change and are valid
  useEffect(() => {
    const {
      targetValue,
      adjustmentMethod,
      roundingDirection,
      roundingIncrement,
    } = formData;

    const needsTargetValue = ["EXACT_AMOUNT", "MARGIN_PERCENT"].includes(
      adjustmentMethod
    );
    let parsedTargetValue = 0;

    if (needsTargetValue) {
      if (!String(targetValue).trim()) {
        clearTargetError();
        setCalculatedValues((prev) => ({ ...prev, adjusted: {}, notes: {} }));
        return;
      }

      const { error, parsedValue } = validateTargetValue(
        adjustmentMethod,
        targetValue
      );
      if (error) {
        setErrors((prev) => ({ ...prev, targetValue: error }));
        setCalculatedValues((prev) => ({ ...prev, adjusted: {}, notes: {} }));
        return;
      }

      clearTargetError();
      parsedTargetValue = parsedValue;
    } else {
      clearTargetError();
    }

    if (currentTotals.totalCost <= 0) {
      setCalculatedValues((prev) => ({ ...prev, adjusted: {}, notes: {} }));
      return;
    }

    try {
      const result = calculateProfitAdjustment({
        roundingIncrement,
        roundingDirection,
        adjustmentMethod,
        targetValue: parsedTargetValue,
        projectTotal: currentTotals.projectTotal,
      });

      setCalculatedValues((prev) => ({
        ...prev,
        adjusted: {
          totalCost: result.adjustedTotalCost,
          profitMargin: result.adjustedProfitMargin,
          adjustableMargin: result.adjustableMargin,
          tenderTotal: result.adjustedTenderTotal,
          difference: result.difference,
          totalProfit: result.adjustedTotalProfit,
        },
        notes: result.notes || {},
      }));
      setErrors((prev) => {
        if (!prev.targetValue) {
          return prev;
        }
        const { targetValue: _target, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      setErrors((prev) => ({
        ...prev,
        targetValue: e.message || "Invalid value",
      }));
      setCalculatedValues((prev) => ({ ...prev, adjusted: {}, notes: {} }));
    }
  }, [formData, currentTotals]);

  const handleInputChange = (field, value) => {
    if (field === "adjustmentMethod") {
      setFormData((prev) => ({
        ...prev,
        adjustmentMethod: value,
        targetValue: "",
      }));
      setErrors({});
      return;
    }

    if (field === "targetValue") {
      const sanitizedValue = sanitizeTargetValue(value, formData.targetValue);
      setFormData((prev) => ({ ...prev, targetValue: sanitizedValue }));
      clearTargetError();
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyClick = () => {
    if (errors.targetValue) return;

    const needsTargetValue = ["EXACT_AMOUNT", "MARGIN_PERCENT"].includes(
      formData.adjustmentMethod
    );
    if (needsTargetValue) {
      if (!String(formData.targetValue).trim()) {
        setErrors((prev) => ({ ...prev, targetValue: "Enter a value" }));
        return;
      }

      const { error } = validateTargetValue(
        formData.adjustmentMethod,
        formData.targetValue
      );
      if (error) {
        setErrors((prev) => ({ ...prev, targetValue: error }));
        return;
      }
    }

    setShowApplyConfirmation(true);
  };

  const handleApplyConfirm = async () => {
    setShowApplyConfirmation(false);
    setIsLoading(true);
    try {
      // Prepare request data
      const requestData = {
        adjustmentMethod: formData.adjustmentMethod,
        roundingIncrement: formData.roundingIncrement,
        roundingDirection: formData.roundingDirection,
        totalCost: currentTotals.totalCost,
        originalTotal: currentTotals.totalSaleAmount,
        originalMargin: currentTotals.totalProfitPercentage,
        originalIndividualMargin: currentTotals.individualProfitMargin,
      };

      // For FINAL_AMOUNT, send 0 (not used by backend but required field)
      // For EXACT_AMOUNT and MARGIN_PERCENT, send the actual target value
      const requiresTargetValue = ["EXACT_AMOUNT", "MARGIN_PERCENT"].includes(
        formData.adjustmentMethod
      );
      requestData.targetValue = requiresTargetValue
        ? formData.targetValue
        : calculatedValues.adjusted.tenderTotal;

      await onApplyAdjustment(requestData);

      // Hard refresh the project summary so tender totals reflect line-item updates
      await getProjectSummaryData({ projectId, dispatch });
      setFormData((prev) => ({ ...prev, targetValue: "" }));
    } catch (error) {
      console.error("Failed to apply adjustment:", error);
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyCancel = () => {
    setShowApplyConfirmation(false);
  };

  const handleResetClick = () => {
    if (!projectId) {
      console.error("Project ID is required for reset");
      return;
    }
    setShowResetConfirmation(true);
  };

  const handleResetConfirm = async () => {
    setShowResetConfirmation(false);
    setIsLoading(true);
    try {
      // Call the reset API to restore original values from audit trail
      await dispatch(resetProfitMargin(projectId));

      // Reset form to default values
      setFormData({
        roundingIncrement: 1000,
        roundingDirection: "UP",
        adjustmentMethod: "EXACT_AMOUNT",
        targetValue: "",
      });
      await getProjectSummaryData({ projectId, dispatch });

      // Clear any errors and warnings
      setErrors({});

      // Clear adjusted values (will be refreshed when project data updates)
      setCalculatedValues((prev) => ({
        ...prev,
        adjusted: {},
        notes: {},
      }));

      // Close the modal to trigger project data refresh
      onClose();
    } catch (error) {
      console.error("Failed to reset profit margin:", error);
      // Error handling is done by the API call
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetCancel = () => {
    setShowResetConfirmation(false);
  };

  const getFieldLabel = () => {
    if (formData.adjustmentMethod === "EXACT_AMOUNT") {
      return "Target Tender Amount (exact)";
    } else if (formData.adjustmentMethod === "MARGIN_PERCENT") {
      return "Target Margin %";
    }
    return ""; // Not shown for FINAL_AMOUNT
  };

  const getFieldPlaceholder = () => {
    if (formData.adjustmentMethod === "EXACT_AMOUNT") {
      return "Enter exact target amount";
    } else if (formData.adjustmentMethod === "MARGIN_PERCENT") {
      return "Enter target margin percentage";
    }
    return ""; // Not shown for FINAL_AMOUNT
  };

  // Determine if rounding controls should be shown
  const showRoundingControls = ["FINAL_AMOUNT", "MARGIN_PERCENT"].includes(
    formData.adjustmentMethod
  );

  // Determine if target value field should be shown
  const showTargetValueField = ["EXACT_AMOUNT", "MARGIN_PERCENT"].includes(
    formData.adjustmentMethod
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="profit-margin-adjustment-modal"
      fullWidth={true}
      maxWidth="md"
      modalHeading="Profit Margin Adjustment"
      showActionBtn={false}
      dataCy="profit-margin-adjustment-modal"
    >
      <div
        className="profit-margin-adjustment-modal"
        data-cy="profit-margin-adjustment-content"
      >
        <Row className="mb-4">
          <Col md={showRoundingControls ? 4 : showTargetValueField ? 6 : 12}>
            <Form.Group>
              <Form.Label data-cy="adjustment-method-label">
                Adjustment Method
              </Form.Label>
              <Form.Select
                data-cy="adjustment-method-select"
                value={formData.adjustmentMethod}
                onChange={(e) =>
                  handleInputChange("adjustmentMethod", e.target.value)
                }
                disabled={isLoading}
              >
                <option value="EXACT_AMOUNT">Target Amount</option>
                <option value="FINAL_AMOUNT">Round Current Total</option>
                <option value="MARGIN_PERCENT">Margin Percent</option>
              </Form.Select>
            </Form.Group>
          </Col>
          {showRoundingControls && (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label data-cy="rounding-increment-label">
                    Rounding Increment
                  </Form.Label>
                  <Form.Select
                    data-cy="rounding-increment-select"
                    value={formData.roundingIncrement}
                    onChange={(e) =>
                      handleInputChange(
                        "roundingIncrement",
                        parseInt(e.target.value)
                      )
                    }
                    disabled={isLoading}
                  >
                    <option value={1000}>$1,000</option>
                    <option value={10000}>$10,000</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label data-cy="rounding-direction-label">
                    Rounding Direction
                  </Form.Label>
                  <Form.Select
                    data-cy="rounding-direction-select"
                    value={formData.roundingDirection}
                    onChange={(e) =>
                      handleInputChange("roundingDirection", e.target.value)
                    }
                    disabled={isLoading}
                  >
                    <option value="UP">Round Up</option>
                    <option value="DOWN">Round Down</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </>
          )}
          {showTargetValueField && (
            <Col
              md={showRoundingControls ? 4 : 6}
              className={showRoundingControls ? "mt-3" : ""}
            >
              <Form.Group>
                <Form.Label data-cy="target-value-label">
                  {getFieldLabel()}
                </Form.Label>
                <Form.Control
                  data-cy="target-value-input"
                  type="text"
                  value={formData.targetValue}
                  onChange={(e) =>
                    handleInputChange("targetValue", e.target.value)
                  }
                  placeholder={getFieldPlaceholder()}
                  isInvalid={!!errors.targetValue}
                  disabled={isLoading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.targetValue}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          )}
        </Row>

        {/* {warnings.length > 0 && (
                    <Alert variant="warning" className="mb-3">
                        <ul className="mb-0">
                            {warnings.map((w, i) => (
                                <li key={i}>{w.message}</li>
                            ))}
                        </ul>
                    </Alert>
                )} */}

        <div className="mb-4">
          <Table
            className="profit-margin-table"
            data-cy="profit-margin-preview-table"
          >
            <thead>
              <tr>
                <th></th>
                <th>Original</th>
                <th>Adjusted</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Cost</td>
                <td>{toUSD(calculatedValues.original.totalCost)}</td>
                <td>{toUSD(calculatedValues.adjusted.totalCost)}</td>
                <td>{calculatedValues.notes.totalCost || ""}</td>
              </tr>
              <tr>
                <td>Profit Margin %</td>
                <td>{showPercent(calculatedValues.original.profitMargin)}</td>
                <td>{showPercent(calculatedValues.adjusted.profitMargin)}</td>
                <td>{calculatedValues.notes.profitMargin || ""}</td>
              </tr>
              {/* <tr>
                                <td>Adjustable Margin% (Mat + Subcon)</td>
                                <td></td>
                                <td>{showPercent(calculatedValues.adjusted.adjustableMargin)}</td>
                                <td>Applied uniformly to materials & subcontractors; labor unchanged</td>
                            </tr> */}
              <tr>
                <td>Tender Total</td>
                <td>{toUSD(calculatedValues.original.tenderTotal)}</td>
                <td>{toUSD(calculatedValues.adjusted.tenderTotal)}</td>
                <td>{calculatedValues.notes.tenderTotal || ""}</td>
              </tr>
              <tr>
                <td>Difference</td>
                <td>
                  {typeof calculatedValues.original.difference === "string"
                    ? calculatedValues.original.difference
                    : ""}
                </td>
                <td>
                  {Number.isFinite(calculatedValues.adjusted.difference)
                    ? calculatedValues.adjusted.difference > 0
                      ? `+${toUSD(calculatedValues.adjusted.difference)}`
                      : toUSD(calculatedValues.adjusted.difference)
                    : ""}
                </td>
                <td>{calculatedValues.notes.difference || ""}</td>
              </tr>
              <tr>
                <td>Total Profit</td>
                <td>{toUSD(calculatedValues.original.totalProfit)}</td>
                <td>{toUSD(calculatedValues.adjusted.totalProfit)}</td>
                <td>{calculatedValues.notes.totalProfit || ""}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Audit History Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0" data-cy="audit-history-title">
              Adjustment History
            </h6>
            <Button
              data-cy="audit-history-toggle-btn"
              variant="outline-secondary"
              className="grey-outline"
              size="sm"
              onClick={() => setShowAuditHistory(!showAuditHistory)}
            >
              {showAuditHistory ? "Hide History" : "Show History"}
            </Button>
          </div>

          {showAuditHistory && (
            <div className="audit-history-section">
              {auditHistory.length > 0 ? (
                <CustomTable
                  dataCy="audit-history-table"
                  columns={[
                    {
                      field: "created_at",
                      title: "Date",
                      class: "text-nowrap",
                      render: (row) =>
                        new Date(row.created_at).toLocaleDateString(),
                    },
                    {
                      field: "user_name",
                      title: "User",
                      class: "",
                      render: (row) => row.user_name,
                    },
                    {
                      field: "adjustment_method",
                      title: "Method",
                      class: "text-nowrap",
                      render: (row) =>
                        formatAdjustmentMethod(row.adjustment_method),
                    },
                    {
                      field: "target_value",
                      title: "Target",
                      class: "text-end",
                      render: (row) => toUSD(row.target_value),
                    },
                    {
                      field: "original_total",
                      title: "Original Total",
                      class: "text-end",
                      render: (row) => toUSD(row.original_total),
                    },
                    {
                      field: "adjusted_total",
                      title: "Adjusted Total",
                      class: "text-end",
                      render: (row) => toUSD(row.adjusted_total),
                    },
                    {
                      field: "original_margin",
                      title: "Original Margin",
                      class: "text-end",
                      render: (row) => showPercent(row.original_margin),
                    },
                    {
                      field: "adjusted_margin",
                      title: "Adjusted Margin",
                      class: "text-end",
                      render: (row) => showPercent(row.adjusted_margin),
                    },
                    {
                      field: "rounding_increment",
                      title: "Increment",
                      class: "text-end",
                      render: (row) =>
                        `$${row.rounding_increment?.toLocaleString()}`,
                    },
                    {
                      field: "rounding_direction",
                      title: "Direction",
                      class: "",
                      render: (row) => row.rounding_direction,
                    },
                  ]}
                  data={auditHistory}
                  toolbar={false}
                  enableSearch={false}
                  addButton={false}
                  exportButton={false}
                  itemsPerPage={10}
                  itemsPerPageCustom={true}
                  stickyHeader={true}
                  emptyMessage="No adjustment history found for this project."
                />
              ) : (
                <Alert
                  variant="info"
                  className="mb-0"
                  data-cy="no-audit-history-message"
                >
                  No adjustment history found for this project.
                </Alert>
              )}
            </div>
          )}
        </div>

        <div className="d-flex">
          <Button
            data-cy="apply-adjustment-btn"
            variant="primary"
            className="btn-bg-brand-1 border-0"
            onClick={handleApplyClick}
            disabled={
              !!errors.targetValue ||
              (showTargetValueField && !formData.targetValue) ||
              isLoading
            }
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                APPLYING...
              </>
            ) : (
              "APPLY ADJUSTMENTS"
            )}
          </Button>
          <Button
            data-cy="reset-to-original-btn"
            variant="outline-secondary"
            className="ms-2 grey-outline"
            onClick={handleResetClick}
            disabled={isLoading}
          >
            RESET TO ORIGINAL
          </Button>
        </div>

        {/* Apply Confirmation Modal */}
        <Modal
          open={showApplyConfirmation}
          onClose={handleApplyCancel}
          aria-labelledby="apply-confirmation-modal"
          maxWidth="sm"
          modalHeading="Confirm Apply Adjustments"
          showActionBtn={false}
          data-cy="apply-confirmation-modal"
        >
          <div className="p-4">
            <p className="mb-3">
              Are you sure you want to apply these profit margin adjustments?
              This will update all tender line items and cannot be undone.
            </p>
            <div className="mb-3">
              <strong>Summary:</strong>
              <ul className="mb-0 mt-2">
                <li>
                  Original Total: {toUSD(calculatedValues.original.tenderTotal)}
                </li>
                <li>
                  Adjusted Total: {toUSD(calculatedValues.adjusted.tenderTotal)}
                </li>
                <li>
                  Original Margin:{" "}
                  {showPercent(calculatedValues.original.profitMargin)}
                </li>
                <li>
                  Adjusted Margin:{" "}
                  {showPercent(calculatedValues.adjusted.profitMargin)}
                </li>
              </ul>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                data-cy="apply-cancel-btn"
                variant="outline-secondary"
                onClick={handleApplyCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                data-cy="apply-confirm-btn"
                variant="primary"
                className="btn-bg-brand-1 border-0"
                onClick={handleApplyConfirm}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Applying...
                  </>
                ) : (
                  "Apply Adjustments"
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Reset Confirmation Modal */}
        <Modal
          open={showResetConfirmation}
          onClose={handleResetCancel}
          aria-labelledby="reset-confirmation-modal"
          maxWidth="sm"
          modalHeading="Confirm Reset to Original"
          showActionBtn={false}
          data-cy="reset-confirmation-modal"
        >
          <div className="p-4">
            <p className="mb-3">
              Are you sure you want to reset the profit margin to its original
              value? This will restore all tender line items to their previous
              state and cannot be undone.
            </p>
            <div className="mb-3">
              <strong>Current State:</strong>
              <ul className="mb-0 mt-2">
                <li>
                  Current Total: {toUSD(calculatedValues.original.tenderTotal)}
                </li>
                <li>
                  Current Margin:{" "}
                  {showPercent(calculatedValues.original.profitMargin)}
                </li>
              </ul>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                data-cy="reset-cancel-btn"
                variant="outline-secondary"
                onClick={handleResetCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                data-cy="reset-confirm-btn"
                variant="primary"
                className="btn-bg-brand-1 border-0"
                onClick={handleResetConfirm}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Resetting...
                  </>
                ) : (
                  "Reset to Original"
                )}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Modal>
  );
}
